import type {
  GroupedBillingCandidateOrder,
  GroupedBillingInputBody,
  GroupedBillingListItem,
} from "@cx/contracts";
import { MAX_GROUPED_ORDERS, MIN_GROUPED_ORDERS } from "@cx/domain";
import type { Tone } from "@cx/ui";

/** Faturamento agrupado (T2.7): textos e regras da tela, sem React (testados em labels.test.ts). */

export type GroupedForm = {
  date: string;
  paymentTermId: string;
  paymentMethodId: string;
  taxOperationId: string;
  issueInvoice: boolean;
  postReceivables: boolean;
  notes: string;
};

export const emptyGroupedForm = (
  date: string,
  can: { invoice: boolean; receivables: boolean },
): GroupedForm => ({
  date,
  paymentTermId: "",
  paymentMethodId: "",
  taxOperationId: "",
  issueInvoice: can.invoice,
  postReceivables: can.receivables,
  notes: "",
});

/** Soma dos pedidos marcados (centavos inteiros; o total do pedido já é seguro). */
export const selectedTotal = (
  orders: readonly GroupedBillingCandidateOrder[],
  ids: ReadonlySet<string>,
): number => orders.reduce((a, o) => (ids.has(o.id) ? a + o.totalCents : a), 0);

/** Condição em comum dos pedidos marcados (padrão do formulário), ou "". */
export function sharedTerm(
  orders: readonly GroupedBillingCandidateOrder[],
  ids: ReadonlySet<string>,
): string {
  const picked = orders.filter((o) => ids.has(o.id));
  const first = picked[0]?.paymentTermId ?? null;
  return first && picked.every((o) => o.paymentTermId === first) ? first : "";
}

/** O que impede faturar agora (mensagem para o usuário), ou null. A API confere tudo de novo. */
export function groupedFormProblem(
  orders: readonly GroupedBillingCandidateOrder[],
  ids: ReadonlySet<string>,
  f: GroupedForm,
  today: string,
): string | null {
  if (ids.size < MIN_GROUPED_ORDERS)
    return `Selecione ao menos ${MIN_GROUPED_ORDERS} pedidos do mesmo cliente.`;
  if (ids.size > MAX_GROUPED_ORDERS) return `No máximo ${MAX_GROUPED_ORDERS} pedidos por faturamento.`;
  if (!f.issueInvoice && !f.postReceivables) return "Escolha gerar a NF-e, as contas a receber ou os dois.";
  if (!f.date) return "Informe a data do faturamento.";
  if (f.date > today) return "A data do faturamento não pode ser futura.";
  const settled = orders.find((o) => ids.has(o.id) && o.receivablesSettled);
  if (f.postReceivables && settled)
    return `O pedido nº ${settled.number} tem conta a receber com baixa. Estorne a baixa ou fature só a NF-e.`;
  return null;
}

export function groupedBody(
  contactId: string,
  ids: readonly string[],
  f: GroupedForm,
): GroupedBillingInputBody {
  return {
    contactId,
    salesOrderIds: [...ids],
    date: f.date,
    paymentTermId: f.paymentTermId || null,
    paymentMethodId: f.paymentMethodId || null,
    taxOperationId: f.issueInvoice ? f.taxOperationId || null : null,
    issueInvoice: f.issueInvoice,
    postReceivables: f.postReceivables,
    notes: f.notes.trim() || null,
  };
}

export const groupedOutputs = (
  g: Pick<GroupedBillingListItem, "issueInvoice" | "postReceivables">,
): string =>
  g.issueInvoice && g.postReceivables ? "NF-e e contas" : g.issueInvoice ? "Só NF-e" : "Só contas";

export const groupedState = (g: { reversedAt: string | null }): { label: string; tone: Tone } =>
  g.reversedAt ? { label: "Desfeito", tone: "secondary" } : { label: "Em vigor", tone: "success" };

/** O que o desfazer faz, conforme o que o faturamento gerou. */
export function reverseExplanation(
  g: Pick<GroupedBillingListItem, "issueInvoice" | "postReceivables">,
): string {
  const parts = [
    g.postReceivables &&
      "as contas a receber do faturamento são canceladas e as dos pedidos voltam a ser lançadas (parcela com baixa impede)",
    g.issueInvoice && "a NF-e precisa estar cancelada ou rejeitada antes",
  ].filter(Boolean);
  return `Os pedidos voltam a ficar livres para faturar; ${parts.join("; ")}. Nada é excluído: fica o registro do estorno.`;
}
